// /login — login del backoffice.
//
// Redirige al location.state.from, o a la primera pantalla que el rol pueda
// ver (lib/access.ts): no todos los roles entran por el panel.

import { FormEvent, useEffect, useState } from 'react';
import { emailInputProps, normalizeEmail } from '@/lib/email-normalize';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';
import { adminLogin, AdminApiError, getSession } from '@/lib/api';
import { ensureDocsPass } from '@/help/docs';
import { fieldCls } from '@/lib/form-styles';
import { landingPath } from '@/lib/access';

interface LocationStateFrom {
  from?: string;
}

/**
 * Destino pedido por `?next=`. Quien lo manda es el porton del manual: una
 * pagina de /docs sin pase valido rebota acá con la ruta original a cuestas.
 *
 * Solo se aceptan rutas de ESTE sitio. Sin este filtro, un enlace armado a
 * mano (`/login?next=https://otro-sitio/`) convertiria la pantalla de ingreso
 * del backoffice en un trampolin creible hacia cualquier lado. `//` tambien se
 * descarta: el navegador lo lee como un host, no como una ruta.
 */
function safeNext(search: string): string | null {
  const raw = new URLSearchParams(search).get('next');
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

/** ¿El destino sale de React? El manual es estatico: hay que recargar. */
function isOutsideTheApp(path: string): boolean {
  return path.startsWith('/docs');
}

/**
 * Lleva al destino pedido. Para el manual, primero deja el pase puesto: sin
 * eso el porton vuelve a rebotar al login y el usuario queda en un bucle sin
 * entender por que, habiendo escrito bien la contraseña.
 */
async function goTo(next: string, navigate: NavigateFunction): Promise<void> {
  if (!isOutsideTheApp(next)) {
    navigate(next, { replace: true });
    return;
  }
  await ensureDocsPass();
  // El ancla no viaja en la request, pero el navegador la conserva sobre la
  // URL de destino de la redireccion. Reponerla acá es lo que hace que el
  // enlace profundo de la ayuda sobreviva al rodeo por el ingreso.
  window.location.replace(`${next}${window.location.hash}`);
}

function errorMessageFor(code: string): string {
  switch (code) {
    case 'invalid_api_key':
    case 'invalid_credentials':
      return 'Email o contraseña incorrectos.';
    case 'rate_limited':
      return 'Demasiados intentos. Esperá un momento.';
    case 'network_error':
      return 'No pudimos conectar. Revisá tu conexión e intentá de nuevo.';
    case 'internal_error':
      return 'Tuvimos un problema de nuestro lado. Probá de nuevo en un momento.';
    default:
      return 'No pudimos completar el login.';
  }
}

export default function AdminLogin(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const next = safeNext(location.search);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Caso tipico: el manual se abrio en una pestaña nueva, el pase habia
  // vencido, y el porton mando acá — pero la sesion del backoffice sigue viva.
  // Pedir la contraseña de nuevo ahi seria una molestia sin ninguna ganancia.
  useEffect(() => {
    if (next && getSession()) void goTo(next, navigate);
  }, [navigate, next]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      // Capa 3 — submit: se vuelve a forzar la forma canónica. El pool de
      // admins arrastra la misma case-sensitivity que el de usuarios.
      const session = await adminLogin(normalizeEmail(email), password);
      const state = location.state as LocationStateFrom | null;
      // Sin destino pedido, cada rol entra por donde tiene algo que hacer.
      // Si `from` apunta a una seccion que no le corresponde, la puerta de esa
      // ruta lo reacomoda igual.
      await goTo(next ?? state?.from ?? landingPath(session), navigate);
    } catch (e) {
      const code = e instanceof AdminApiError ? e.code : 'unknown';
      setErr(errorMessageFor(code));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      id="main"
      className="min-h-full flex flex-col items-center justify-center px-4 py-10"
    >
      <section
        data-help="login.form"
        aria-labelledby="admin-login-title"
        className="w-full max-w-md bg-surface dark:bg-surface-dark rounded-2xl shadow-lg p-8 space-y-6 border border-surface-border dark:border-surface-dark-border"
      >
        <header className="space-y-2 text-center">
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">
            AuthSys Backoffice
          </p>
          <h1 id="admin-login-title" className="text-2xl font-semibold">
            Ingresar al equipo
          </h1>
          <p className="text-sm text-ink-muted">
            {next?.startsWith('/docs')
              ? 'El manual es parte del backoffice: ingresá y te llevamos a la página que pediste.'
              : 'Solo para miembros del equipo interno.'}
          </p>
        </header>

        <form
          onSubmit={onSubmit}
          aria-busy={loading ? 'true' : undefined}
          className="space-y-4"
          noValidate
        >
          <label htmlFor="email" className="block text-sm space-y-1">
            <span className="font-medium">Email</span>
            <input
              id="email"
              name="email"
              type="email"
              required
              autoFocus
              autoComplete="username"
              value={email}
              {...emailInputProps(setEmail)}
              className={fieldCls({ size: 'lg' })}
            />
          </label>
          <label htmlFor="password" className="block text-sm space-y-1">
            <span className="font-medium">Contraseña</span>
            <input
              id="password"
              name="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={fieldCls({ size: 'lg' })}
            />
          </label>

          {err ? (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
            >
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading}
            aria-busy={loading ? 'true' : undefined}
            className="w-full min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium py-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-light disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? 'Ingresando…' : 'Ingresar'}
          </button>
        </form>
      </section>
    </main>
  );
}
